import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'tr_active_timer';

export interface TimerState {
  active: boolean;
  caseRef: string;
  startTime: number; // epoch ms
  elapsed: number; // ms since startTime
  taskDescription: string;
  manualUnits: string;
  activityCodeId: string | null;
  billable: boolean;
  pos: { x: number; y: number };
}

interface TimerStartOpts {
  caseRef: string;
  taskDescription: string;
  manualUnits?: string;
  activityCodeId?: string | null;
  billable?: boolean;
}

interface TimerSnapshot {
  caseRef: string;
  startTime: number;
  endTime: number;
  elapsed: number;
  taskDescription: string;
  manualUnits: string;
  activityCodeId: string | null;
  billable: boolean;
}

interface TimerContextValue {
  timer: TimerState;
  startTimer: (opts: TimerStartOpts) => boolean;
  stopTimer: () => TimerSnapshot | null;
  updatePos: (pos: { x: number; y: number }) => void;
  setField: <
    K extends keyof Pick<
      TimerState,
      'taskDescription' | 'manualUnits' | 'activityCodeId' | 'billable'
    >,
  >(
    field: K,
    value: TimerState[K],
  ) => void;
}

const IDLE_STATE: TimerState = {
  active: false,
  caseRef: '',
  startTime: 0,
  elapsed: 0,
  taskDescription: '',
  manualUnits: '',
  activityCodeId: null,
  billable: true,
  pos: { x: 80, y: 80 },
};

function loadFromStorage(): TimerState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return IDLE_STATE;
    const parsed = JSON.parse(raw) as Partial<TimerState>;
    if (!parsed.active || !parsed.startTime || !parsed.caseRef) return IDLE_STATE;
    return {
      ...IDLE_STATE,
      ...parsed,
      // Recalculate elapsed on restore
      elapsed: Date.now() - parsed.startTime,
    };
  } catch {
    return IDLE_STATE;
  }
}

function persistToStorage(state: TimerState) {
  if (!state.active) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }
}

const TimerContext = createContext<TimerContextValue | null>(null);

export function TimerProvider({ children }: { children: ReactNode }) {
  const [timer, setTimer] = useState<TimerState>(loadFromStorage);
  // Ref mirrors state so stopTimer can read synchronously without relying
  // on the React 19 concurrent-mode updater being called inline.
  const timerRef = useRef<TimerState>(timer);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Tick every second while active
  useEffect(() => {
    if (timer.active && timer.startTime) {
      tickRef.current = setInterval(() => {
        setTimer((prev) => ({ ...prev, elapsed: Date.now() - prev.startTime }));
      }, 1000);
      return () => {
        if (tickRef.current) clearInterval(tickRef.current);
      };
    }
    return undefined;
  }, [timer.active, timer.startTime]);

  // Keep ref in sync so stopTimer can read synchronously
  const syncRef = useCallback((next: TimerState) => {
    timerRef.current = next;
  }, []);

  const startTimer = useCallback(
    (opts: TimerStartOpts): boolean => {
      if (timerRef.current.active) return false;
      setTimer((prev) => {
        if (prev.active) return prev;
        const next: TimerState = {
          active: true,
          caseRef: opts.caseRef,
          startTime: Date.now(),
          elapsed: 0,
          taskDescription: opts.taskDescription,
          manualUnits: opts.manualUnits ?? '',
          activityCodeId: opts.activityCodeId ?? null,
          billable: opts.billable ?? true,
          pos: prev.pos,
        };
        persistToStorage(next);
        syncRef(next);
        return next;
      });
      return true;
    },
    [syncRef, timerRef],
  );

  const stopTimer = useCallback((): TimerSnapshot | null => {
    const current = timerRef.current;
    if (!current.active) return null;

    const snapshot: TimerSnapshot = {
      caseRef: current.caseRef,
      startTime: current.startTime,
      endTime: Date.now(),
      elapsed: current.elapsed,
      taskDescription: current.taskDescription,
      manualUnits: current.manualUnits,
      activityCodeId: current.activityCodeId,
      billable: current.billable,
    };
    const next = { ...IDLE_STATE, pos: current.pos };
    persistToStorage(next);
    syncRef(next);
    setTimer(next);
    return snapshot;
  }, [syncRef]);

  // Sync timer state from other browser tabs writing to the same localStorage key
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next = e.newValue ? loadFromStorage() : { ...IDLE_STATE, pos: timerRef.current.pos };
      setTimer(next);
      syncRef(next);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [syncRef]);

  const updatePos = useCallback(
    (pos: { x: number; y: number }) => {
      setTimer((prev) => {
        const next = { ...prev, pos };
        syncRef(next);
        return next;
      });
    },
    [syncRef],
  );

  const setField = useCallback(
    <
      K extends keyof Pick<
        TimerState,
        'taskDescription' | 'manualUnits' | 'activityCodeId' | 'billable'
      >,
    >(
      field: K,
      value: TimerState[K],
    ) => {
      setTimer((prev) => {
        const next = { ...prev, [field]: value };
        if (next.active) persistToStorage(next);
        syncRef(next);
        return next;
      });
    },
    [syncRef],
  );

  return (
    <TimerContext.Provider value={{ timer, startTimer, stopTimer, updatePos, setField }}>
      {children}
    </TimerContext.Provider>
  );
}

export function useTimer(): TimerContextValue {
  const ctx = useContext(TimerContext);
  if (!ctx) throw new Error('useTimer must be used within TimerProvider');
  return ctx;
}
